/**
 * Which blog articles are live.
 *
 * Public for now: the founder's four pieces (the 2025 launch post, who we're
 * building BOOTH for, the Product Hunt launch, and "If Anyone Can Build
 * Software"), the BOOTH Team's "Turning Chair Gaps Into Additional Rental
 * Revenue", plus the four how-to guides. The other journal pieces are
 * written but launch later, alongside Paul Panama's byline, so they're
 * hidden rather than deleted — the copy, images
 * and per-article routes all stay exactly where they are. Removing a slug
 * from this set publishes that article again, on both /blog and
 * /articles/[slug], with no other change anywhere.
 *
 * Kept as a hide-list rather than a `published: true` flag on each article
 * so that a NEW article is live by default: forgetting to flag a post you
 * just wrote should not silently bury it.
 */
export const HIDDEN_ARTICLE_SLUGS: ReadonlySet<string> = new Set([
  "why-every-chair-rents-on-its-own",
  "urban-barbershop-performance-tracking",
  "why-our-first-ad-is-deliberately-scrappy",
  "search-booth-rentals-from-claude-or-chatgpt",
  // Launching later, with Paul Panama.
  "six-metrics-every-urban-shop-owner-needs",
  "turn-chair-data-into-shop-decisions",
  "document-booth-rent-to-unlock-funding",
])

/** False for an article that is written but not yet published. */
export function isArticleVisible(slug: string): boolean {
  return !HIDDEN_ARTICLE_SLUGS.has(slug)
}

/** Every article that exists, published or not. Keep in sync with
    lib/blog-article-bodies.ts and lib/blog-articles.ts. */
export const ALL_ARTICLE_SLUGS: readonly string[] = [
  "turning-chair-gaps-into-additional-rental-revenue",
  "if-anyone-can-build-software",
  "making-the-barbershop-searchable",
  "who-we-are-building-booth-for",
  "why-our-first-ad-is-deliberately-scrappy",
  "search-booth-rentals-from-claude-or-chatgpt",
  "six-metrics-every-urban-shop-owner-needs",
  "turn-chair-data-into-shop-decisions",
  "urban-barbershop-performance-tracking",
  "document-booth-rent-to-unlock-funding",
  "which-link-actually-pays-you",
  "why-every-chair-rents-on-its-own",
  "how-to-sign-a-booth-agreement",
  "how-to-collect-rent-on-time",
  "earn-and-redeem-booth-rewards",
  "booth-transforming-the-barbershop-ecosystem-of-tomorrow",
]

/**
 * True when at least one article is live. Footers gate their /blog link on
 * this, so publishing an article again restores the link everywhere without
 * anyone remembering to put it back.
 */
export function hasVisibleArticles(): boolean {
  return ALL_ARTICLE_SLUGS.some(isArticleVisible)
}

/**
 * Old article slugs and the slug each one became. A slug should read as its
 * title, so when a title changes the URL follows it — and the old URL keeps
 * working, permanently redirected here, for links already shared or indexed.
 * Add to this map; never remove from it.
 */
export const RENAMED_ARTICLE_SLUGS: Readonly<Record<string, string>> = {
  "features-can-be-copied-networks-cant": "if-anyone-can-build-software",
  "booth-launches-on-product-hunt": "making-the-barbershop-searchable",
  "boothmcp-search-from-claude-chatgpt": "search-booth-rentals-from-claude-or-chatgpt",
  "six-metrics-urban-shop-owners": "six-metrics-every-urban-shop-owner-needs",
  "turn-chair-data-into-decisions": "turn-chair-data-into-shop-decisions",
  "document-booth-rent-for-funding": "document-booth-rent-to-unlock-funding",
  "booth-transforming-the-barbershop-ecosystem-of-tomorow": "booth-transforming-the-barbershop-ecosystem-of-tomorrow",
}
